import { z } from "zod/v4";

// The federation classifies athletes as female or male, and leaves the field as
// an empty string on rows the scraper could not classify. `catch` degrades an
// empty or unrecognised value to `undefined` so one unclassified athlete cannot
// fail the whole search response.
export const athleteGenderSchema = z.enum(["female", "male"]);

export type AthleteGender = z.infer<typeof athleteGenderSchema>;

// One hit from the discovery directory search (Meilisearch-backed). Extra index
// fields are ignored — only what the DVV picker + standalone search tool need is
// modelled here. `best_points_value` is the numeric DVV/LV points of the athlete's
// primary ranking (0 = unranked); `license_number` is populated once discovery has
// re-indexed (older docs carry it as undefined until the next sync).
// `rank_trend` is the federation's own glyph for the primary ranking's movement
// (DVV ships arrows, not signed numbers) and `last_result_date` is ISO `YYYY-MM-DD`.
export const athleteDirectoryHitSchema = z.object({
	age_category: z.string().optional(),
	best_list: z.string().optional(),
	best_points: z.string().optional(),
	best_points_value: z.number().optional(),
	best_rank: z.number().optional(),
	club: z.string().optional(),
	display_name: z.string().optional(),
	federation: z.string(),
	first_name: z.string(),
	gender: athleteGenderSchema.optional().catch(undefined),
	id: z.string(),
	image_url: z.string().optional(),
	last_name: z.string(),
	last_result_date: z.string().optional(),
	license_number: z.string().nullable().optional(),
	podium_count: z.number().optional(),
	rank_trend: z.string().optional(),
	slug: z.string(),
});

export type AthleteDirectoryHit = z.infer<typeof athleteDirectoryHitSchema>;

export const athleteDirectorySearchResponseSchema = z.object({
	items: z.array(athleteDirectoryHitSchema),
	total: z.number().int().optional(),
});

export const scrapedAthleteSchema = z.object({
	age_category: z.string().nullable().optional(),
	claimed_at: z.coerce.date().nullable().optional(),
	claimed_by: z.string().nullable().optional(),
	club: z.string().nullable().optional(),
	created_at: z.coerce.date(),
	excluded_at: z.coerce.date().nullable().optional(),
	federation: z.string(),
	federation_url: z.string().nullable().optional(),
	first_name: z.string(),
	gender: z.string().nullable().optional(),
	id: z.string(),
	image_url: z.string().nullable().optional(),
	instagram_handle: z.string().nullable().optional(),
	last_name: z.string(),
	last_scraped_at: z.coerce.date(),
	license_number: z.string().nullable().optional(),
	rankings: z.array(
		z.object({
			list: z.string(),
			list_name: z.string().optional(),
			partner: z.string(),
			points: z.string(),
			rank: z.string(),
			season: z.string(),
			trend: z.string(),
		}),
	),
	scrape_checksum: z.string(),
	slug: z.string(),
	source: z.string(),
	source_id: z.string(),
	updated_at: z.coerce.date(),
});

export const ATHLETE_REMOVAL_EMAIL_MAX_LENGTH = 254;
export const athleteRemovalConfirmationSchema = z.object({
	email: z.email("Enter a valid email address.").max(ATHLETE_REMOVAL_EMAIL_MAX_LENGTH),
});
export type AthleteRemovalConfirmation = z.infer<typeof athleteRemovalConfirmationSchema>;

export const scrapeRunSchema = z.object({
	athletes_created: z.number(),
	athletes_found: z.number(),
	athletes_updated: z.number(),
	error_message: z.string().nullable().optional(),
	finished_at: z.coerce.date().nullable().optional(),
	id: z.string(),
	source: z.string(),
	started_at: z.coerce.date(),
	status: z.enum(["pending", "running", "completed", "failed"]),
});

export const rankingListSyncSchema = z.object({
	age_category: z.string(),
	athletes_found: z.number(),
	checksum: z.string(),
	created_at: z.coerce.date(),
	gender: z.string(),
	id: z.string(),
	last_synced_at: z.coerce.date(),
	list_id: z.string(),
	list_type: z.string(),
	series: z.string(),
	updated_at: z.coerce.date(),
});

export const scraperSourceSchema = z.object({
	id: z.string(),
	last_run: scrapeRunSchema.nullable().optional(),
	name: z.string(),
});

export const claimRequestSchema = z.object({
	athlete_id: z.string(),
	created_at: z.coerce.date(),
	first_name: z.string(),
	id: z.string(),
	last_name: z.string(),
	podium_count: z.number(),
	reviewed_at: z.coerce.date().nullable().optional(),
	reviewed_by: z.string().nullable().optional(),
	slug: z.string(),
	status: z.enum(["pending", "approved", "rejected"]),
	updated_at: z.coerce.date(),
	user_email: z.string(),
	user_id: z.string(),
	user_name: z.string(),
});

export type ClaimRequest = z.infer<typeof claimRequestSchema>;

export const removalStatusSchema = z.enum(["pending", "approved", "rejected"]);
export type RemovalStatus = z.infer<typeof removalStatusSchema>;

export const removalRequestSchema = z.object({
	athlete_id: z.string(),
	created_at: z.coerce.date(),
	email: z.string(),
	first_name: z.string(),
	id: z.string(),
	last_name: z.string(),
	reason: z.string().nullable().optional(),
	reviewed_at: z.coerce.date().nullable().optional(),
	slug: z.string(),
	status: removalStatusSchema,
	updated_at: z.coerce.date(),
});
export type RemovalRequest = z.infer<typeof removalRequestSchema>;

export const syncResultSchema = z.object({
	indexed: z.number(),
});

// A slug-suffixed athlete ("ole-sackermann-2") and the row it was split off
// from. Discovery decides which pairs are the same person; the portal only
// shows them and asks for the merge.
export const athleteDuplicatePairSchema = z.object({
	canonical_id: z.string(),
	canonical_slug: z.string(),
	duplicate_id: z.string(),
	duplicate_slug: z.string(),
});

export type AthleteDuplicatePair = z.infer<typeof athleteDuplicatePairSchema>;

export const athleteDuplicatesSchema = z.object({
	pairs: z.array(athleteDuplicatePairSchema),
	total: z.number(),
});

export const mergeAthleteDuplicatesResultSchema = z.object({
	failed: z.array(athleteDuplicatePairSchema),
	merged: z.number(),
	pairs: z.array(athleteDuplicatePairSchema),
});
